import type { LbEventVarInfo } from '@uefacom-newdev/liveblog-integration-react';
import type {
  Match as PkMatch,
  Person,
  Player,
  Team,
  PlayerEvent as MatchScorer,
  Round,
  League,
  RelatedMatch,
  Winner as MatchWin,
  Competition,
  Matchday,
  Group,
  Translatable,
  Translation,
} from '@uefadigital/panenka';
import {
  EventType,
  EventSubType,
  PlayerEventSubType,
  WinnerReason as MatchWinReason,
  MatchWinner,
  MatchStatus,
  MatchPhase,
  MatchType,
  TeamType,
  RoundMode,
  RoundPhase,
  RoundStatus,
  LineupStatus,
  TeamCategory,
} from '@uefadigital/panenka';

import type { Enum } from './enum.types';

export type { Person, Player, Team, MatchScorer, Round, League, RelatedMatch, MatchWin, Group };
export {
  EventType,
  EventSubType,
  PlayerEventSubType,
  MatchWinReason,
  MatchWinner,
  MatchStatus,
  MatchPhase,
  MatchType,
  TeamType,
  RoundMode,
  RoundPhase,
  RoundStatus,
  LineupStatus,
  TeamCategory,
};

export const PlayerPosition = {
  UNKNOWN: 'UNKNOWN',
  DEFENDER: 'DEFENDER',
  FORWARD: 'FORWARD',
  GOALKEEPER: 'GOALKEEPER',
  MIDFIELDER: 'MIDFIELDER',
} as const;
export type PlayerPosition = Enum<typeof PlayerPosition>;

export type HomeAway = 'home' | 'away';

export type SubstitutionStatus = 'in' | 'out';

export type LineupsPlayer = {
  player: Player;
  type: string;
  jerseyNumber: number;
  fspFieldCoordinate: {
    x: number;
    y: number;
  };
  status?: PlayerStatus;
};

export const CoachRole = {
  ASSISTANT_COACH: 'ASSISTANT_COACH',
  COACH: 'COACH',
  GOALKEEPER_COACH: 'GOALKEEPER_COACH',
  SECOND_ASSISTANT_COACH: 'SECOND_ASSISTANT_COACH',
} as const;
export type CoachRole = Enum<typeof CoachRole>;

export type LineupsCoach = {
  person?: Person;
  role?: CoachRole;
};

export type LineupsTeam = {
  bench: LineupsPlayer[];
  field: LineupsPlayer[];
  coaches: LineupsCoach[];
  unavailable: LineupsPlayer[];
  team: Team;
  shirtColor: string;
};

export type Lineups = {
  homeTeam?: LineupsTeam;
  awayTeam?: LineupsTeam;
};

export type PlayerEvent = {
  type?: EventType;
  subType?: EventSubType;
  phase?: MatchPhase;
  id: string;
  primaryActor?: {
    person?: Player;
    team?: Team;
  };
  secondaryActor?: {
    person?: Player;
    team?: Team;
  };
  time?: {
    injuryMinute?: number;
    minute: number;
    second?: number;
  };
  varInfo?: LbEventVarInfo;
  timestamp?: string;
  injuryTimeMinutesAdded?: number;
};

export type Match = PkMatch & {
  playerOfTheMatch?: {
    player?: Player;
    teamId?: string;
  };
  streamUrl?: {
    [lang: string]: string;
  };
  highlightsVideo?: {
    id: string;
  };
};

export type LiveMatch = Match & {
  hash?: string;
  loaded?: boolean;
};

export type MatchCalendarDate = {
  date: string;
  numberOfMatches: number;
};

export type CompetitionStructure = {
  competition: Competition;
  matchdays: Matchday[];
  rounds: Round[];
  season: any;
};

export type PlayerInfo = {
  player: Player & { clubJerseyNumber?: string; nationalJerseyNumber?: string };
  status: PlayerStatus;
};

export interface PlayerStatus extends Translatable {
  healthStatus: 'INJURED' | 'FIT' | 'DOUBTFUL';
  isSuspended: boolean;
  isBooked: boolean;

  injuryType: string;
  matchAttendance: string;
  trainingPerformance: string;

  translated: boolean;
  translations: {
    healthStatus: Translation;
    matchAttendance: Translation;
    trainingPerformance: Translation;
  };
}
